<!-- @format -->
<template>
    <div class="widget one-to-one-email-widget">
        <div class="widget-header">
            <div class="title-icon-container">
                <k4-email-svg />
                <h3>{{ steps[step].title }}</h3>
            </div>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper" v-if="!isAccountsLoading">
            <div v-show="step === 'email' || step === 'edit'" class="email-step">
                <one-to-one-email-composer
                    ref="composer"
                    :accounts-available="accountsAvailable"
                    :message-id="messageId"
                    :company-id="companyId"
                    :is-to-editable="isToEditable"
                    :is-cc-editable="isCcEditable"
                    :is-bc-editable="isBccEditable"
                    :initial-tos="initialTos"
                    :initial-bccs="initialBccs"
                    :initial-ccs="initialCcs"
                    :initial-subject="initialSubject"
                    :initial-body="initialBody"
                    :initial-from="initialFrom"
                    :initial-attachments="initialAttachments"
                    :initial-is-scheduled="initialIsScheduled"
                    :initial-send-date="initialSendDate"
                />
            </div>
            <div v-show="step === 'saving' || step === 'sending'" class="saving-step">
                <loading-screen-svg></loading-screen-svg>
            </div>
        </div>
        <div
            v-show="step === 'email' && !hasGraphicalTemplate && !isComposerLoading"
            class="app-add-ons"
        >
            <div class="add-on text-email-icon" @click="openEmailTemplates">
                <text-email-svg class="svg-container" />
                <div class="tooltip text-tooltip" data-tooltip="Text Templates"></div>
            </div>
            <div class="add-on graphic-email-icon" @click="openGraphicEmailTemplates">
                <graphic-email-svg class="svg-container" />
                <div
                    class="tooltip graphical-tooltip"
                    data-tooltip="Graphical Email Templates"
                ></div>
            </div>
            <div class="add-on book-icon" @click="openLibrary">
                <r21-library-outline-svg class="svg-container" />
                <div class="tooltip library-tooltip" data-tooltip="Marketing Collateral"></div>
            </div>
            <div class="add-on" @click="handleInsertAttachment">
                <e8-attach-svg />
                <div class="tooltip attachment-tooltip" data-tooltip="Attachments"></div>
            </div>
            <div class="add-on" @click="handleInsertImage">
                <a10-image-file-stroke-svg />
                <div class="tooltip image-tooltip" data-tooltip="Insert Image"></div>
            </div>
            <div class="add-on">
                <emoji-dropdown
                    position="top-right"
                    @input="appendEmoji($event)"
                    @is-open="testing"
                />
                <div
                    :class="[{ 'no-tooltip': isDropdownOpen }, 'tooltip emoji-tooltip']"
                    data-tooltip="Emoticons"
                ></div>
            </div>
            <div class="add-on" @click="createInsertSignatureEvent">
                <signature-svg class="svg-container"></signature-svg>
                <div class="tooltip signature-tooltip" data-tooltip="Signature"></div>
            </div>
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" @click="closeWidget">Cancel</app-button>
            </div>
            <div class="action-container">
                <button class="btn secondary-btn schedule-btn" @click="openDatePickerWidget">
                    Schedule <span class="svg-container"><clock-svg /></span>
                </button>
                <!-- <app-button :loading="steps[step].loading" @click="saveDraft">
                    Save as Draft
                </app-button> -->
                <app-button
                    class="send-btn"
                    :loading="steps[step].loading"
                    @click="steps[step].action"
                >
                    {{ steps[step].buttonText }}
                    <l21-campaign-sent-svg />
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const TextEmailSvg = () => import('@/components/svg/text-email-svg.vue');
const GraphicEmailSvg = () => import('@/components/svg/graphic-email-svg.vue');
const K4EmailSvg = () => import('@/components/svg/grid/k4-email-svg');
const R21LibraryOutlineSvg = () => import('@/components/svg/grid/r21-library-outline-svg');
const A10ImageFileStrokeSvg = () => import('@/components/svg/grid/a10-image-file-stroke-svg');
const l21CampaignSentSVG = () => import('@/components/svg/grid/l21-campaign-sent-svg');
const E8AttachSvg = () => import('@/components/svg/grid/e8-attach-svg');
const SignatureSvg = () => import('@/components/svg/signature-svg.vue');
const EmojiDropdown = () => import('@/components/common/emoji-dropdown');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const SendEmailComposer = () => import('@/components/one-to-one-email/one-to-one-email-composer');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const ClockSvg = () => import('@/components/svg/grid/o2-schedule-outline-svg');
import IntegrationMixin from '@/mixins/integrations-mixin';

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'one-to-one-email-widget',

    components: {
        'app-button': AppButton,
        'text-email-svg': TextEmailSvg,
        'graphic-email-svg': GraphicEmailSvg,
        'k4-email-svg': K4EmailSvg,
        'r21-library-outline-svg': R21LibraryOutlineSvg,
        'a10-image-file-stroke-svg': A10ImageFileStrokeSvg,
        'e8-attach-svg': E8AttachSvg,
        'l21-campaign-sent-svg': l21CampaignSentSVG,
        'signature-svg': SignatureSvg,
        'emoji-dropdown': EmojiDropdown,
        'widget-close-svg': WidgetCloseSvg,
        'one-to-one-email-composer': SendEmailComposer,
        'loading-screen-svg': LoadingScreenSvg,
        'clock-svg': ClockSvg,
    },

    mixins: [IntegrationMixin],

    props: {
        options: {
            type: Object,
            default: null,
        },

        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            accountsAvailable: [],
            messageId: null,
            isInitializing: true,
            initialTos: [],
            initialBccs: [],
            initialCcs: [],
            isComposerLoading: true,
            isAccountsLoading: false,
            isDropdownOpen: false,
            isToEditable: false,
            isCcEditable: false,
            isBccEditable: false,
            successMessage: '',
            initialSubject: null,
            initialBody: null,
            initialAttachments: null,
            initialFrom: null,
            initialSendDate: null,
            companyId: null,
            hasGraphicalTemplate: false,
            step: null,
            steps: {
                email: {
                    title: 'Send Email',
                    buttonText: 'Send',
                    action: this.sendEmail,
                    loading: false,
                },

                sending: {
                    title: 'Sending',
                    buttonText: 'Sending',
                    action: () => {},
                    loading: true,
                },

                saving: {
                    title: 'Saving',
                    buttonText: 'Saving',
                    action: () => {},
                    loading: true,
                },
            },
        };
    },

    computed: {
        clientId() {
            return this.$store.state.context.client.id;
        },

        userId() {
            return this.$store.state.user.id;
        },

        isClientUser() {
            return this.$store.getters.isClientUser;
        },
    },

    created() {
        this.initialize();
        this.getAccountsAvailable();

        EventBus.$on('email-status-emitted', this.setSuccessMessage);
        EventBus.$on('has-graphic-template', this.graphicalTemplateStatus);
        EventBus.$on('loading-content', this.setLoadingStatus);
    },

    destroyed() {
        EventBus.$off('email-status-emitted');
        EventBus.$off('has-graphic-template', this.graphicalTemplateStatus);
        EventBus.$off('loading-content', this.setLoadingStatus);
    },

    methods: {
        getAccountsAvailable() {
            this.isAccountsLoading = true;
            return new Promise((resolve) => {
                this.getIntegrations((response) => {
                    this.accountsAvailable = response;
                    if (this.accountsAvailable.length === 0) {
                        EventBus.$emit('open-widget', {
                            name: 'email-inbox-connect-widget',
                        });
                        this.closeWidget();
                    }
                    this.isAccountsLoading = false;
                    resolve();
                });
            });
        },

        testing(element) {
            this.isDropdownOpen = element;
        },

        setLoadingStatus(status) {
            this.isComposerLoading = status;
        },

        openEmailTemplates() {
            this.$refs.composer.openEmailTemplates();
        },

        openGraphicEmailTemplates() {
            this.$refs.composer.openGraphicEmailTemplates();
        },

        openLibrary() {
            this.$refs.composer.openLibrary();
        },

        appendEmoji(event) {
            this.$refs.composer.appendEmoji(event);
        },

        createInsertSignatureEvent() {
            this.$refs.composer.createInsertSignatureEvent();
        },

        handleInsertImage() {
            this.$refs.composer.handleInsertImage();
        },

        handleInsertAttachment() {
            this.$refs.composer.handleInsertAttachment();
        },

        initialize() {
            this.setStep();
            this.setData();
        },

        setStep() {
            this.step = 'email';
        },

        setData() {
            this.setMessageOptions();
            this.setTos();
            this.setBccs();
            this.setCcs();
            this.setMessageData();
            this.setAccountsAvailable();

            this.companyId = this.options.companyId || null;
        },

        setMessageOptions() {
            this.isToEditable = this.options.isToEditable || false;
            this.isCcEditable = this.options.isCcEditable || false;
            this.isBccEditable = this.options.isBccEditable || false;
        },

        setTos() {
            this.initialTos = this.options.initialTos || [];
            this.addImagePropertyTo();
        },

        setBccs() {
            this.initialBccs = this.options.initialBccs || [];
            this.addImagePropertyBcc();
        },

        setCcs() {
            this.initialCcs = this.options.initialCcs || [];
            this.addImagePropertyCc();
        },

        setMessageData() {
            this.messageId = this.options.messageId || null;
            this.initialSubject = this.options.initialSubject || null;
            this.initialBody = this.options.initialBody || null;
            this.initialAttachments = this.options.initialAttachments || [];
            this.initialFrom =
                this.options.initialFrom || (this.isClientUser && this.userId) ? this.userId : null;
            this.initialIsScheduled = this.options.isScheduled || null;
            this.initialSendDate = this.options.initialSendDate || null;
        },

        setAccountsAvailable() {
            const areTherAccountsWithoutImage = this.accountsAvailable.some(
                ({ profilePicture }) => profilePicture === null,
            );

            if (areTherAccountsWithoutImage) {
                this.addUserImages();
            }
        },

        addUserImages() {
            return new Promise((resolve, reject) => {
                const filter = `$filter=ClientId eq ${this.clientId}`;
                const select = '$select=Id,ProfileImageUrl';
                const restUrl = `/service/Users?${filter}&${select}&$count=true`;

                this.$api
                    .get(restUrl)
                    .then((response) => {
                        const users = response.data.value.map((user) => {
                            return {
                                userId: user.Id,
                                profilePicture: user.ProfileImageUrl,
                            };
                        });

                        this.accountsAvailable.forEach((account) => {
                            if (account.profilePicture === null) {
                                const userWithMatchedId = users.find(
                                    ({ userId }) => userId === account.createdBy,
                                );
                                if (userWithMatchedId) {
                                    account.profilePicture = userWithMatchedId.profilePicture;
                                }
                            }
                        });
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching some account images',
                            { error },
                        );
                        reject();
                    });
            });
        },

        addImagePropertyTo() {
            for (var i = 0; i < this.initialTos.length; i++) {
                if (
                    !this.initialTos[i].hasOwnProperty('imageUrl') &&
                    this.initialTos[i].hasOwnProperty('profileImageUrl')
                ) {
                    this.initialTos[i].imageUrl = this.initialTos[i].profileImageUrl;
                }
            }
        },

        addImagePropertyBcc() {
            for (var i = 0; i < this.initialBccs.length; i++) {
                if (
                    !this.initialBccs[i].hasOwnProperty('imageUrl') &&
                    this.initialBccs[i].hasOwnProperty('profileImageUrl')
                ) {
                    this.initialBccs[i].imageUrl = this.initialBccs[i].profileImageUrl;
                }
            }
        },

        addImagePropertyCc() {
            for (var i = 0; i < this.initialCcs.length; i++) {
                if (
                    !this.initialCcs[i].hasOwnProperty('imageUrl') &&
                    this.initialCcs[i].hasOwnProperty('profileImageUrl')
                ) {
                    this.initialCcs[i].imageUrl = this.initialCcs[i].profileImageUrl;
                }
            }
        },

        graphicalTemplateStatus(status) {
            this.hasGraphicalTemplate = status;
        },

        saveDraft() {
            var isValidAsDraft = this.$refs.composer.isValidAsDraft();

            if (isValidAsDraft) {
                this.step = 'saving';
                this.$refs.composer.hideError();
                this.$refs.composer.hideDraftError();
                var scheduled = false;

                if (this.messageId) {
                    this.updateEmail(scheduled)
                        .then(() => {
                            this.closeWidget();
                            this.throwRefreshMessagesEvent();
                        })
                        .catch((error) => {
                            this.closeWidget();
                            this.$helpers.showFailNotification(
                                'An error occurred while updating your draft.',
                                { error },
                            );
                        });
                } else {
                    this.saveNewDraft(scheduled);
                }
            } else {
                this.isInitializing = false;
                this.$refs.composer.showDraftError();
            }
        },

        sendEmail() {
            var scheduled = true;
            var isValid = this.$refs.composer.isValid();

            if (isValid) {
                this.step = 'sending';
                this.$refs.composer.hideError();

                if (this.messageId) {
                    this.updateEmail(scheduled).then(() => {
                        this.closeWidget();
                        this.throwRefreshMessagesEvent();
                        var notification = this.createSuccessNotification();
                        EventBus.$emit('show-notification', notification);
                    });
                } else {
                    this.sendNewEmail(scheduled);
                }
            } else {
                this.$refs.composer.showError();
            }
        },

        updateEmail(scheduled) {
            return new Promise((resolve, reject) => {
                this.isInitializing = true;

                var isValid = this.$refs.composer.isValid();
                if (isValid) {
                    this.step = 'saving';
                    this.$refs.composer.hideError();

                    this.$refs.composer
                        .updateEmail(scheduled)
                        .then(() => {
                            resolve();
                        })
                        .catch((error) => {
                            this.$helpers.showFailNotification(
                                'An error occurred while updating your email.',
                                { error },
                            );
                            reject();
                        })
                        .finally(() => {
                            this.isInitializing = false;
                        });
                } else {
                    this.isInitializing = false;
                    this.$refs.composer.showError();
                    reject();
                }
            });
        },

        throwRefreshMessagesEvent() {
            EventBus.$emit('refresh-messages');
        },

        saveNewDraft(scheduled) {
            this.$refs.composer
                .saveEmail(scheduled)
                .then(() => {
                    this.throwRefreshMessagesEvent();
                    this.$helpers.showSuccessNotification('Your draft has been saved.');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.step = 'email';
                    this.$helpers.showFailNotification(
                        'An error occurred while saving your email.',
                        { error },
                    );
                });
        },

        sendNewEmail(scheduled) {
            return new Promise((resolve, reject) => {
                this.$refs.composer
                    .saveEmail(scheduled)
                    .then(() => {
                        var notification = this.createSuccessNotification();
                        EventBus.$emit('show-notification', notification);

                        this.closeWidget();
                        this.throwRefreshMessagesEvent();
                        resolve();
                    })
                    .catch((error) => {
                        this.step = 'email';
                        this.$helpers.showFailNotification(
                            'An error occurred while sending your email.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        setSuccessMessage(sendDate) {
            if (sendDate) {
                this.successMessage =
                    'Email successfully scheduled for <strong>' +
                    this.$moment(sendDate).format('MMM Do, YYYY [at] h:mm a') +
                    '</strong>.';
            } else {
                this.successMessage = 'Email successfully sent.';
            }
        },

        createSuccessNotification() {
            var notification = {
                iconUrl: require('@/assets/images/envelope-blue-gradient.png'),
                isIconSvg: true,
                subIcon: 'success',
                message: this.successMessage,
                actionLabel: null,
                action: () => {},
            };

            return notification;
        },

        openDatePickerWidget() {
            var widget = {
                name: 'date-picker-widget',
            };

            EventBus.$emit('open-widget', widget);
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
.one-to-one-email-widget .app-add-ons {
    display: flex;
    justify-content: flex-start;
    position: absolute;
    bottom: -1px;
    left: 0;
    z-index: 1;
    background-color: #fff;
    width: 100%;
    position: absolute;
    bottom: 85px;
}

.one-to-one-email-widget .add-on {
    padding: 5px 15px;
}

.one-to-one-email-widget .add-on:hover {
    cursor: pointer;
}

.one-to-one-email-widget .add-on .svg-container {
    width: 30px;
    height: 30px;
}

.one-to-one-email-widget .add-on .svg-container path,
.one-to-one-email-widget .add-on .svg-container rect {
    transition:
        fill 250ms ease-in,
        stroke 250ms ease-in;
}

.one-to-one-email-widget .add-on:hover .svg-container path,
.one-to-one-email-widget .add-on:hover .svg-container .a,
.one-to-one-email-widget .add-on:hover .svg-container rect {
    fill: var(--color-fill-accent-skyblue-medium); /*mass replace */
}

.one-to-one-email-widget.widget.purple-gradient-header .widget-header {
    background: linear-gradient(264deg, #e38dee, #7a5bff);
}

.one-to-one-email-widget .widget-header .title-icon-container {
    display: flex;
}

.one-to-one-email-widget .widget-header .title-icon-container .k4-email-svg {
    margin-right: 17px;
}

.one-to-one-email-widget .widget-header .title-icon-container .k4-email-svg .a {
    fill: #ffffff;
}

.one-to-one-email-widget .schedule-btn .svg-container path {
    fill: #18aeff;
}

.one-to-one-email-widget .add-on.text-email-icon:hover .svg-container .a {
    stroke: var(--color-fill-accent-skyblue-medium); /*mass replace */
}

.one-to-one-email-widget .add-on.text-email-icon .svg-container {
    width: 20px;
}

.one-to-one-email-widget .add-on.text-email-icon .svg-container,
.one-to-one-email-widget .add-on.graphic-email-icon .svg-container {
    width: 20px;
}

.one-to-one-email-widget .add-on.book-icon .svg-container {
    width: 25px;
}

.one-to-one-email-widget .add-on:hover .svg-container.r21-library-outline-svg path {
    fill: none;
    stroke: var(--color-fill-accent-skyblue-medium); /*mass replace */
}

.one-to-one-email-widget .add-on .emoji-dropdown {
    margin-top: 6px;
}

.one-to-one-email-widget .add-on .e8-attach-svg {
    width: 16.8px;
    height: auto;
    margin-top: 6px;
    transform: rotate(180deg);
}

.one-to-one-email-widget .add-on .e8-attach-svg path {
    stroke: #877d88;
}

.one-to-one-email-widget .add-on:hover .e8-attach-svg path {
    stroke: var(--color-fill-accent-skyblue-medium); /*mass replace */
}

.one-to-one-email-widget .add-on:hover .emoji-dropdown svg path {
    stroke: var(--color-fill-accent-skyblue-medium); /*mass replace */
}

.one-to-one-email-widget .add-on .a10-image-file-stroke-svg {
    margin-top: 5px;
}

.one-to-one-email-widget .add-on:hover .emoji-dropdown svg path {
    stroke: var(--color-fill-accent-skyblue-medium); /*mass replace */
}

.one-to-one-email-widget .add-on:hover .a10-image-file-stroke-svg path,
.one-to-one-email-widget .add-on:hover .a10-image-file-stroke-svg circle {
    stroke: var(--color-fill-accent-skyblue-medium); /*mass replace */
}

.one-to-one-email-widget .add-on .a10-image-file-stroke-svg path {
    fill: none;
    stroke: #877d88;
}

.one-to-one-email-widget .add-on .a10-image-file-stroke-svg circle {
    fill: none;
    stroke: #877d88;
}

.one-to-one-email-widget .emoji-dropdown .emoji-dropdown-container.display-top-right {
    right: -305px;
    top: -405px;
}

/* TOOLTIPS */

.one-to-one-email-widget .add-on {
    position: relative;
}

.one-to-one-email-widget .add-on .tooltip {
    position: absolute;
    visibility: hidden;
    opacity: 0;
    transform: translateY(10px);
    transition:
        transform 200ms ease,
        opacity 200ms;
    z-index: 99;
    top: -30px;
    left: 0;
}

.one-to-one-email-widget .add-on .tooltip.text-tooltip {
    left: 10px;
}

.one-to-one-email-widget .add-on .tooltip.graphical-tooltip {
    left: -30px;
}

.one-to-one-email-widget .add-on .tooltip::before {
    content: attr(data-tooltip);
    background: #ffffff;
    color: #382153;
    position: absolute;
    font-size: 12px;
    line-height: 14px;
    padding: 4px 8px;
    border-radius: 12px;
    border: 1px solid #e9e9ef;
    white-space: nowrap;
    box-shadow: 0px 3px 6px rgba(130, 116, 232, 0.2);
}

.one-to-one-email-widget .add-on .tooltip::after {
    width: 8px;
    height: 8px;
    border: 1px solid transparent;
    border-left-color: #e9e9ef;
    border-bottom-color: #e9e9ef;
    content: '';
    position: absolute;
    transform: rotate(-45deg);
    background: #ffffff;
    box-shadow: none;
    top: 20px;
    left: 0;
}

.one-to-one-email-widget .add-on .tooltip.text-tooltip::after {
    left: 10px;
}

.one-to-one-email-widget .add-on .tooltip.graphical-tooltip::after {
    left: 50px;
}

.one-to-one-email-widget .add-on .tooltip.signature-tooltip::after {
    left: 28px;
}

.one-to-one-email-widget .add-on .tooltip.library-tooltip::after {
    left: 23px;
}

.one-to-one-email-widget .add-on .tooltip.emoji-tooltip::after {
    left: 20px;
}

.one-to-one-email-widget .add-on .tooltip.attachment-tooltip::after {
    left: 22px;
}

.one-to-one-email-widget .add-on .tooltip.image-tooltip::after {
    left: 20px;
}

.one-to-one-email-widget .add-on:hover .tooltip {
    visibility: visible;
    opacity: 1;
    transform: translateY(0px);
}

.one-to-one-email-widget .add-on:hover .tooltip.no-tooltip.emoji-tooltip {
    visibility: hidden;
}

.one-to-one-email-widget .team-signatures {
    position: absolute;
    width: 250px;
    background: #ffffff;
    z-index: 10000;
    box-shadow: 0px 3px 12px rgba(130, 116, 232, 0.2);
    border-radius: 12px;
}

.one-to-one-email-widget .team-signatures .team-signature {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: 7px 20px;
}

.one-to-one-email-widget .team-signatures .team-signature:first-child {
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
}

.one-to-one-email-widget .team-signatures .team-signature:last-child {
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
}

.one-to-one-email-widget .team-signatures .team-signature:hover {
    background: #e9e9ef;
}

.one-to-one-email-widget .team-signatures .team-signature img {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    margin-right: 20px;
}

.one-to-one-email-widget .send-btn .l21-campaign-sent-svg path {
    fill: #fff;
}

.one-to-one-email-widget .one-to-one-email-composer .app-text-editor .app-add-ons {
    display: none;
}

.one-to-one-email-widget .one-to-one-email-composer .attachments-container {
    margin-bottom: 48px;
}
</style>

<style scoped>
.one-to-one-email-widget .saving-step {
    text-align: center;
}

.one-to-one-email-widget .content-wrapper {
    padding: 0;
}

.one-to-one-email-widget .action-container {
    display: flex;
}

.one-to-one-email-widget .action-container .btn {
    margin-left: 15px;
}

.one-to-one-email-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    color: #382153;
    border: 1px solid #cac6cb;
    border-radius: 20px;
    width: 120px;
}

.one-to-one-email-widget .cancel-container .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.one-to-one-email-widget .action-container .btn.white-btn {
    background: #ffffff;
    color: #877d88;
    border: 2px solid #e9e9ef;
}

.one-to-one-email-widget .secondary-btn {
    color: #18aeff;
    height: 40px;
    border-radius: 20px;
    padding: 0px 25px;
    font-size: 14px;
    line-height: 17px;
    min-width: 0;
    background-color: transparent;

    transition: background-color 250ms ease-in;
}

.one-to-one-email-widget .secondary-btn:hover {
    background-color: #dbedfa;
}

.one-to-one-email-widget .send-btn {
    width: 124px;
}

.one-to-one-email-widget .send-btn .l21-campaign-sent-svg {
    margin-left: 12px;
}

.one-to-one-email-widget .schedule-btn {
    background: transparent;
    color: #18aeff;
    border: 1px solid #18aeff;
    border-radius: 20px;
    padding-right: 45px;
    position: relative;
    width: 124px;
}

.one-to-one-email-widget .schedule-btn .svg-container {
    position: absolute;
    width: 14px;
    top: 10px;
    right: 18px;
}

.one-to-one-email-widget .schedule-btn .svg-container .o2-schedule-outline-svg {
    width: 17.5px;
    height: auto;
}
</style>
